import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  setDoc,
  getDocs, 
  query, 
  where, 
  orderBy,
  deleteDoc,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

// Family roles
export const FAMILY_ROLES = {
  PARENT: 'parent',
  CHILD: 'child',
  NANNY: 'nanny',
  DRIVER: 'driver'
};

// Helper function to get user name from Firebase users collection
const getUserNameInternal = async (userId, fallbackEmail) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.firstName && userData.lastName) {
        return `${userData.firstName} ${userData.lastName}`;
      } else if (userData.firstName) {
        return userData.firstName;
      } else if (userData.lastName) {
        return userData.lastName;
      }
    }
    // Fallback to email if no name found
    return fallbackEmail ? fallbackEmail.split('@')[0] : 'User';
  } catch (error) {
    console.error('Error fetching user name:', error);
    return fallbackEmail ? fallbackEmail.split('@')[0] : 'User';
  }
};

// Default permissions for all new users (not role-based)
const getDefaultPermissions = () => {
  return {
    canViewAllEvents: true,  // Can see all family events by default
    canEditEvents: true      // Can create and edit events by default
  };
};

// Create a new family and assign the creator as admin
export const createFamily = async (userId, userEmail, userName) => {
  try {
    console.log('👨‍👩‍👧‍👦 Creating family for:', { userId, userEmail, userName });
    
    // Get the actual name from users collection
    const actualUserName = await getUserNameInternal(userId, userEmail);
    console.log('📝 Using actual name from DB:', actualUserName);
    
    const parentPermissions = getDefaultPermissions();
    
    const familyData = {
      adminUserId: userId,
      adminEmail: userEmail,
      adminName: actualUserName,
      createdAt: new Date(),
      members: [
        {
          userId,
          email: userEmail,
          name: actualUserName,
          role: FAMILY_ROLES.PARENT,
          status: 'accepted',
          joinedAt: new Date(),
          permissions: parentPermissions
        }
      ]
    };

    const familyRef = await addDoc(collection(db, 'families'), familyData);
    console.log('✅ Family created with ID:', familyRef.id);
    
    // Update user with family ID - create document if it doesn't exist
    try {
      await updateDoc(doc(db, 'users', userId), {
        familyId: familyRef.id,
        familyRole: FAMILY_ROLES.PARENT,
        permissions: parentPermissions
      });
      console.log('✅ User updated with family ID');
    } catch (updateError) {
      // If user document doesn't exist, create it
      if (updateError.code === 'not-found') {
        console.log('📝 User document not found, creating new user document');
        await setDoc(doc(db, 'users', userId), {
          email: userEmail,
          name: actualUserName,
          familyId: familyRef.id,
          familyRole: FAMILY_ROLES.PARENT,
          permissions: parentPermissions,
          createdAt: new Date()
        });
        console.log('✅ User document created with family ID');
      } else {
        throw updateError;
      }
    }

    return { success: true, familyId: familyRef.id };
  } catch (error) {
    console.error('Error creating family:', error);
    return { error: 'Failed to create family' };
  }
};

// Send family invitation
export const sendFamilyInvitation = async (senderUserId, senderEmail, senderName, receiverEmail, receiverName, role) => {
  try {
    // Check if sender has a family, if not create one
    console.log('👤 Checking if sender has family:', senderUserId);
    const userDoc = await getDoc(doc(db, 'users', senderUserId));
    let familyId = userDoc.data()?.familyId;
    console.log('📋 User data:', userDoc.data());
    console.log('👨‍👩‍👧‍👦 Existing family ID:', familyId);

    if (!familyId) {
      console.log('🆕 No family found, creating new family');
      const createResult = await createFamily(senderUserId, senderEmail, senderName);
      if (createResult.error) {
        console.log('❌ Failed to create family:', createResult.error);
        return createResult;
      }
      familyId = createResult.familyId;
      console.log('✅ New family created with ID:', familyId);
    } else {
      console.log('✅ Using existing family ID:', familyId);
      // Verify the family document actually exists
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      if (!familyDoc.exists()) {
        console.log('⚠️ Family ID exists in user but family document missing. Recreating...');
        const createResult = await createFamily(senderUserId, senderEmail, senderName);
        if (createResult.error) {
          console.log('❌ Failed to recreate family:', createResult.error);
          return createResult;
        }
        familyId = createResult.familyId;
        console.log('✅ Family recreated with new ID:', familyId);
      }
    }

    // Check if user is already invited or member
    const invitationsQuery = query(
      collection(db, 'familyInvitations'),
      where('familyId', '==', familyId),
      where('receiverEmail', '==', receiverEmail)
    );
    const existingInvitations = await getDocs(invitationsQuery);

    if (!existingInvitations.empty) {
      return { error: 'User already invited to this family' };
    }

    // Create invitation
    const invitationData = {
      familyId,
      senderUserId,
      senderEmail,
      senderName,
      receiverEmail,
      receiverName,
      role,
      status: 'pending',
      createdAt: new Date()
    };

    console.log('📨 Creating invitation:', invitationData);
    const docRef = await addDoc(collection(db, 'familyInvitations'), invitationData);
    console.log('✅ Invitation created with ID:', docRef.id);
    return { success: true };
  } catch (error) {
    console.error('Error sending family invitation:', error);
    return { error: 'Failed to send invitation' };
  }
};

// Get family invitations for a user (received)
export const getFamilyInvitations = async (userEmail) => {
  try {
    console.log('🔍 Fetching family invitations for:', userEmail);
    const q = query(
      collection(db, 'familyInvitations'),
      where('receiverEmail', '==', userEmail),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    const invitations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('📨 Found invitations:', invitations);
    return invitations;
  } catch (error) {
    console.error('Error getting family invitations:', error);
    return [];
  }
};

// Get sent invitations for a user
export const getSentInvitations = async (senderUserId) => {
  try {
    console.log('📤 Fetching sent invitations for:', senderUserId);
    const q = query(
      collection(db, 'familyInvitations'),
      where('senderUserId', '==', senderUserId)
    );
    
    const snapshot = await getDocs(q);
    const invitations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).sort((a, b) => {
      // Sort by createdAt descending (most recent first)
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate() - a.createdAt.toDate();
    });
    
    console.log('📤 Found sent invitations:', invitations);
    return invitations;
  } catch (error) {
    console.error('Error getting sent invitations:', error);
    return [];
  }
};

// Accept family invitation
export const acceptFamilyInvitation = async (invitationId, userId, userEmail, userName) => {
  try {
    console.log('🎯 Accepting invitation:', invitationId);
    const invitationDoc = await getDoc(doc(db, 'familyInvitations', invitationId));
    if (!invitationDoc.exists()) {
      console.log('❌ Invitation document not found');
      return { error: 'Invitation not found' };
    }

    const invitation = invitationDoc.data();
    console.log('📨 Invitation data:', invitation);
    
    // Add user to family
    console.log('🔍 Looking for family:', invitation.familyId);
    const familyDoc = await getDoc(doc(db, 'families', invitation.familyId));
    if (!familyDoc.exists()) {
      console.log('❌ Family document not found for ID:', invitation.familyId);
      return { error: 'Family not found' };
    }

    const familyData = familyDoc.data();
    
    // Get the actual name from users collection
    const actualUserName = await getUserNameInternal(userId, userEmail);
    console.log('📝 Adding member with actual name:', actualUserName);
    
    // Get default permissions (same for all users)
    const memberPermissions = getDefaultPermissions();
    
    const newMember = {
      userId,
      email: userEmail,
      name: actualUserName,
      role: invitation.role,
      status: 'accepted',
      joinedAt: new Date(),
      permissions: memberPermissions
    };

    const updatedMembers = [...familyData.members, newMember];

    // Update family with new member
    await updateDoc(doc(db, 'families', invitation.familyId), {
      members: updatedMembers
    });

    // Update user with family info - create document if it doesn't exist
    try {
      await updateDoc(doc(db, 'users', userId), {
        familyId: invitation.familyId,
        familyRole: invitation.role,
        permissions: memberPermissions
      });
    } catch (updateError) {
      // If user document doesn't exist, create it
      if (updateError.code === 'not-found') {
        console.log('📝 User document not found when accepting invitation, creating new user document');
        await setDoc(doc(db, 'users', userId), {
          email: userEmail,
          name: actualUserName,
          familyId: invitation.familyId,
          familyRole: invitation.role,
          permissions: memberPermissions,
          createdAt: new Date()
        });
        console.log('✅ User document created with family info');
      } else {
        throw updateError;
      }
    }

    // Update invitation status
    await updateDoc(doc(db, 'familyInvitations', invitationId), {
      status: 'accepted',
      acceptedAt: new Date()
    });

    return { success: true, familyId: invitation.familyId };
  } catch (error) {
    console.error('Error accepting family invitation:', error);
    return { error: 'Failed to accept invitation' };
  }
};

// Decline family invitation
export const declineFamilyInvitation = async (invitationId) => {
  try {
    await updateDoc(doc(db, 'familyInvitations', invitationId), {
      status: 'declined',
      declinedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Error declining family invitation:', error);
    return { error: 'Failed to decline invitation' };
  }
};

// Get family members
export const getFamilyMembers = async (familyId) => {
  try {
    const familyDoc = await getDoc(doc(db, 'families', familyId));
    if (!familyDoc.exists()) {
      return [];
    }
    return familyDoc.data().members || [];
  } catch (error) {
    console.error('Error getting family members:', error);
    return [];
  }
};

// Get sent family invitations
export const getSentFamilyInvitations = async (familyId) => {
  try {
    console.log('🔍 Fetching sent invitations for family:', familyId);
    const q = query(
      collection(db, 'familyInvitations'),
      where('familyId', '==', familyId)
    );
    
    const snapshot = await getDocs(q);
    const invitations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('📤 Found sent invitations:', invitations);
    return invitations;
  } catch (error) {
    console.error('Error getting sent family invitations:', error);
    return [];
  }
};

// Remove family member
export const removeFamilyMember = async (familyId, memberUserId) => {
  try {
    const familyDoc = await getDoc(doc(db, 'families', familyId));
    if (!familyDoc.exists()) {
      return { error: 'Family not found' };
    }

    const familyData = familyDoc.data();
    const updatedMembers = familyData.members.filter(member => member.userId !== memberUserId);

    await updateDoc(doc(db, 'families', familyId), {
      members: updatedMembers
    });

    // Remove family info from user
    await updateDoc(doc(db, 'users', memberUserId), {
      familyId: null,
      familyRole: null
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing family member:', error);
    return { error: 'Failed to remove member' };
  }
};

// Get user's family info
export const getUserFamilyInfo = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    if (!userData.familyId) {
      return null;
    }

    const familyDoc = await getDoc(doc(db, 'families', userData.familyId));
    if (!familyDoc.exists()) {
      return null;
    }

    return {
      familyId: userData.familyId,
      role: userData.familyRole,
      family: familyDoc.data()
    };
  } catch (error) {
    console.error('Error getting user family info:', error);
    return null;
  }
};

// Add custom participant to family
export const addCustomParticipantToFamily = async (familyId, participantName, role = FAMILY_ROLES.CHILD) => {
  try {
    const familyDoc = await getDoc(doc(db, 'families', familyId));
    if (!familyDoc.exists()) {
      return { error: 'Family not found' };
    }

    const familyData = familyDoc.data();
    const customParticipants = familyData.customParticipants || [];

    // Check if participant already exists in custom participants
    const existingCustomParticipant = customParticipants.find(p => p.name.toLowerCase() === participantName.toLowerCase());
    if (existingCustomParticipant) {
      return { error: `"${participantName}" is already in your participant list` };
    }

    // Check if participant already exists in family members
    const existingFamilyMember = familyData.members.find(member => 
      member.name.toLowerCase() === participantName.toLowerCase()
    );
    if (existingFamilyMember) {
      return { error: `"${participantName}" is already a family member` };
    }

    // Create new custom participant
    const newParticipant = {
      id: `custom-${Date.now()}`,
      name: participantName.trim(),
      email: `${participantName.trim().toLowerCase().replace(/\s+/g, '')}@custom.local`,
      custom: true,
      role: role,
      addedAt: new Date()
    };

    // Add to custom participants array
    const updatedCustomParticipants = [...customParticipants, newParticipant];

    // Update family document
    await updateDoc(doc(db, 'families', familyId), {
      customParticipants: updatedCustomParticipants
    });

    console.log('Custom participant added to family:', newParticipant);
    return { success: true, participant: newParticipant };
  } catch (error) {
    console.error('Error adding custom participant to family:', error);
    return { error: 'Failed to add participant' };
  }
};

// Remove custom participant from family
export const removeCustomParticipantFromFamily = async (familyId, participantId) => {
  try {
    const familyDoc = await getDoc(doc(db, 'families', familyId));
    if (!familyDoc.exists()) {
      return { error: 'Family not found' };
    }

    const familyData = familyDoc.data();
    const customParticipants = familyData.customParticipants || [];

    // Filter out the participant to remove
    const updatedCustomParticipants = customParticipants.filter(p => p.id !== participantId);

    // Update family document
    await updateDoc(doc(db, 'families', familyId), {
      customParticipants: updatedCustomParticipants
    });

    console.log('Custom participant removed from family:', participantId);
    return { success: true };
  } catch (error) {
    console.error('Error removing custom participant from family:', error);
    return { error: 'Failed to remove participant' };
  }
};

// Mark custom participant as invited
export const markCustomParticipantAsInvited = async (familyId, participantId, invitedEmail) => {
  try {
    const familyDoc = await getDoc(doc(db, 'families', familyId));
    if (!familyDoc.exists()) {
      return { error: 'Family not found' };
    }

    const familyData = familyDoc.data();
    const customParticipants = familyData.customParticipants || [];

    // Find and update the participant
    const updatedCustomParticipants = customParticipants.map(participant => {
      if (participant.id === participantId) {
        return {
          ...participant,
          invited: true,
          invitedEmail: invitedEmail,
          invitedAt: new Date()
        };
      }
      return participant;
    });

    // Update family document
    await updateDoc(doc(db, 'families', familyId), {
      customParticipants: updatedCustomParticipants
    });

    console.log('Custom participant marked as invited:', participantId);
    return { success: true };
  } catch (error) {
    console.error('Error marking custom participant as invited:', error);
    return { error: 'Failed to mark participant as invited' };
  }
};

// Update user permissions
export const updateUserPermissions = async (familyId, userId, permissions) => {
  try {
    // Update permissions in family document
    const familyDoc = await getDoc(doc(db, 'families', familyId));
    if (!familyDoc.exists()) {
      return { error: 'Family not found' };
    }

    const familyData = familyDoc.data();
    const updatedMembers = familyData.members.map(member => {
      if (member.userId === userId) {
        return {
          ...member,
          permissions: {
            ...member.permissions,
            ...permissions
          }
        };
      }
      return member;
    });

    await updateDoc(doc(db, 'families', familyId), {
      members: updatedMembers
    });

    // Update permissions in user document
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const currentPermissions = userDoc.data().permissions || {};
        await updateDoc(doc(db, 'users', userId), {
          permissions: {
            ...currentPermissions,
            ...permissions
          }
        });
      }
    } catch (error) {
      console.error('Error updating user document permissions:', error);
      // Continue even if user document update fails
    }

    console.log('User permissions updated:', { userId, permissions });
    return { success: true };
  } catch (error) {
    console.error('Error updating user permissions:', error);
    return { error: 'Failed to update permissions' };
  }
};

// Export the helper function for use in other components
export { getDefaultPermissions };